'use client';
import {
  type ProGloveDeviceSerial,
  type ProGloveWorkerFeedbackActionId,
  useProGlove,
} from '@/components/proglove/ProgloveProvider';
import { useCallback, useEffect, useRef } from 'react';

export interface ProGloveFeedback {
  /**
   * Sends `feedback!` (vibration, beep, LED) to the paired glove, or to the given
   * one. For a flow's outcome use `resultFeedbackAction[outcome]`.
   */
  feedback: (feedbackActionId: ProGloveWorkerFeedbackActionId, deviceSerial?: ProGloveDeviceSerial) => void;
}

/**
 * ProGlove's `feedback!` command: the worker feels or sees an outcome. Every
 * model supports it; on gloves without a display it is the only signal.
 *
 * `feedback` keeps its identity when the glove changes (it always sends to the
 * glove paired at the time of the call), so an effect giving feedback for a
 * result does not run again on a reconnect.
 */
export default function useProGloveFeedback(): ProGloveFeedback {
  const { workerFeedback } = useProGlove();
  const workerFeedbackRef = useRef(workerFeedback);
  useEffect(() => {
    workerFeedbackRef.current = workerFeedback;
  }, [workerFeedback]);

  const feedback = useCallback<ProGloveFeedback['feedback']>(
    (feedbackActionId, deviceSerial) => workerFeedbackRef.current(feedbackActionId, deviceSerial),
    [],
  );

  return { feedback };
}
